import { useState } from 'react';
import { BookmarkIcon, HeartIcon, LinkIcon, MessageCircleIcon, SendIcon } from 'lucide-react';
import { PostVisual } from './PostVisual';
import { platformMeta } from './socialOptions';
import { fillTokens } from '../marketing/marketingRules';
import type { MarketingDesign } from '../../core/types/marketing';
import type { SocialPlatform, SocialPost } from '../../core/types/social';
type Props = {
  post: SocialPost;
  design?: MarketingDesign;
  platform: SocialPlatform;
  tokens: Record<string, string>;
  avatar: string;
  handle: string;
};

/** Instagram kort de tekst af na ± 125 tekens; tot daar zie je direct. */
const FOLD = 125;

/** Linkkleur van Instagram zelf (platform-UI, geen merkkleur). 5,7:1 op wit. */
const IG_LINK = 'text-[#3b5bdb]';

/** Zo ongeveer ziet het eruit op een telefoon. Geen pixelperfecte kopie, wel de juiste verhoudingen. */
export function PostPreview({
  post,
  design,
  platform,
  tokens,
  avatar,
  handle
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const meta = platformMeta[platform];
  const caption = fillTokens(post.caption, tokens).trim();
  const hashtags = meta.hashtags ? post.hashtags.join(' ') : '';
  const domain = post.link.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const header = (light: boolean) => <div className={`flex items-center gap-2.5 px-3 py-2.5 ${light ? 'text-white' : 'text-ink'}`} data-id="element-4394" data-mp="1qinmtkx1uxai">
      <img src={avatar} alt="" className="h-8 w-8 rounded-full object-cover ring-1 ring-black/5" data-id="element-4395" data-mp="1ljke9qhv28xs" />
      <span className="min-w-0" data-id="element-4396" data-mp="1gsvocfltxk0r">
        <span className="block truncate text-[13px] font-medium leading-tight" data-id="element-4397" data-mp="1gsm0axkchmxp">{handle}</span>
        <span className={`block text-[11px] leading-tight ${light ? 'text-white/70' : 'text-muted'}`} data-id="element-4398" data-mp="1cqh71zxt6sbl">{platform}</span>
      </span>
    </div>;
  if (meta.format === 'story') {
    return <div className="relative mx-auto aspect-[9/16] w-full max-w-[300px] overflow-hidden rounded-[28px] bg-ink shadow-phone" data-id="element-4399" data-mp="12tm7ddpsulvq">
        <PostVisual post={post} design={design} className="absolute inset-0 h-full w-full" data-id="element-4400" data-mp="xvaxx43f2de1" />
        <div className="absolute inset-x-0 top-0 bg-black/25 pt-2" data-id="element-4401" data-mp="o5m7lm1b9j2z">
          <div className="mx-3 h-0.5 overflow-hidden rounded-full bg-white/40" data-id="element-4402" data-mp="t6054a5adamr">
            <div className="h-full w-1/3 bg-white" data-id="element-4403" data-mp="f5pdv602du12" />
          </div>
          {header(true)}
        </div>
        {platform === 'WhatsApp-status' && caption && <p className="absolute inset-x-0 bottom-0 bg-black/45 px-4 py-4 text-center text-[13px] leading-snug text-white" data-id="element-4404" data-mp="j7t08po9uj14">
            {caption}
          </p>}
        {platform === 'Instagram-story' && domain && <span className={`absolute bottom-10 left-1/2 inline-flex max-w-[80%] -translate-x-1/2 items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-[12px] font-medium ${IG_LINK} shadow`} data-id="element-4405" data-mp="1bxswy18mcpsw">
            <LinkIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} data-id="element-4406" data-mp="1pvzpr2ypnzlp" />
            <span className="truncate" data-id="element-4407" data-mp="1ptmy1y35iavg">{domain}</span>
          </span>}
      </div>;
  }
  const short = caption.length > FOLD && !expanded;
  return <div className="mx-auto w-full max-w-[340px] overflow-hidden rounded-[22px] border border-line bg-white shadow-lift" data-id="element-4408" data-mp="3fbtnjwktg4fv">
      {header(false)}
      <PostVisual post={post} design={design} className="aspect-[4/5] w-full" data-id="element-4409" data-mp="3abxqkhajdd9t" />
      {platform === 'Instagram-post' && <div className="flex items-center gap-4 px-3 pt-3 text-ink" aria-hidden="true" data-id="element-4410" data-mp="3k3v1dmxmu380">
          <HeartIcon className="h-5 w-5" strokeWidth={1.75} data-id="element-4411" data-mp="2we0pp8p1ukpc" />
          <MessageCircleIcon className="h-5 w-5" strokeWidth={1.75} data-id="element-4412" data-mp="2we97nf014g7e" />
          <SendIcon className="h-5 w-5" strokeWidth={1.75} data-id="element-4413" data-mp="2rfzc33ae7dl9" />
          <BookmarkIcon className="ml-auto h-5 w-5" strokeWidth={1.75} data-id="element-4414" data-mp="36c6v40a2e8yy" />
        </div>}
      <div className="px-3 pb-4 pt-2 text-[13px] leading-snug text-ink" data-id="element-4415" data-mp="369u5r21z8wbv">
        {caption ? <p className="whitespace-pre-line break-words" data-id="element-4416" data-mp="31fgdv9mix7by">
            {platform === 'Instagram-post' && <span className="font-medium" data-id="element-4417" data-mp="lnsk1n2t3uxd">{handle} </span>}
            {short ? `${caption.slice(0, FOLD).trimEnd()}… ` : caption}
            {short && <button type="button" onClick={() => setExpanded(true)} className="text-muted" data-id="element-4418" data-mp="2mc6s8hwpqjov">
                meer
              </button>}
          </p> : <p className="text-muted" data-id="element-4419" data-mp="2vbbplkwjg6zg">Nog geen tekst…</p>}
        {!short && hashtags && <p className={`mt-2 break-words ${IG_LINK}`} data-id="element-4420" data-mp="30aogqhug007u">{hashtags}</p>}
        {platform !== 'Instagram-post' && domain && <p className={`mt-2 flex items-center gap-1.5 truncate ${IG_LINK}`} data-id="element-4421" data-mp="358yaqp4fkurv">
            <LinkIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} data-id="element-4422" data-mp="3a8kmsv8iw4z3" />
            {domain}
          </p>}
        {platform === 'Instagram-post' && post.firstComment && <p className="mt-3 border-t border-line pt-2 text-muted" data-id="element-4423" data-mp="3j1efpvgw72vf">
            <span className="font-medium text-ink" data-id="element-4424" data-mp="3j0miyhbk6z3k">{handle}</span> {fillTokens(post.firstComment, tokens)}
          </p>}
      </div>
    </div>;
}